<template>
  <div class="standard-page">
    <!-- Header and Actions -->
    <div class="d-flex justify-end mb-8">
      <!-- Actions -->
      <div class="d-flex align-center">
        <!-- Create New Campaign Link -->
        <v-btn
          color="primary"
          depressed
          class="mr-3 shadow--primary"
          @click="shouldShowFormDialog = true"
        >
          <v-icon left>add</v-icon>
          Create Campaign
        </v-btn>

        <!-- Select Input for Filter -->
        <v-select
          class="contain-select-width"
          label="Filter"
          outlined
          dense
          hide-details
          :items="filterOptions"
          v-model="selectedFilter"
        ></v-select>
      </div>
    </div>

    <!-- Data Table -->
    <template v-if="tableItems.length">
      <v-data-table
        :headers="tableHeaders"
        :items="tableItems"
        hide-default-footer
        disable-pagination
        class="elevation-1 custom-table--campaigns-list"
      >
        <!-- rather than use vuetify's tbody, we're customizing it -->
        <template v-slot:body="{ items }">
          <tbody>
            <tr
              v-for="item in items"
              :key="item.id"
            >
              <td>
                {{ item.index }}
              </td>

              <td class="text-no-wrap">
                {{ item.name }}
              </td>

              <td class="text-no-wrap text-center">
                <v-chip
                  :class="item.is_complete ? 'green' : 'primary'"
                  small
                  dark
                >
                  {{ capitalizeString(item.is_complete ? "Completed" : "Active") }}
                </v-chip>
              </td>

              <td class="text-center">
                <v-btn
                  @click="openDescription(item)"
                  color="primary"
                  depressed
                  small
                >
                  <v-icon small left>description</v-icon>
                  View Details
                </v-btn>
              </td>

              <td class="text-center">
                <component
                  :is="computedComponentName"
                  v-model="shouldShowDownloadModal[item.id]"
                  :close-on-content-click="false"
                  :min-width="computedComponentName === 'v-menu' ? 500 : null"
                  @input="handleModalStateChange"
                >
                  <!-- The button that activates the menu/modal -->
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      depressed
                      small
                      v-on="on"
                      v-bind="attrs"
                      @click.passive="downloadForm.name = item.name"
                    >
                      <v-icon small left>download</v-icon>
                      Download Report
                    </v-btn>
                  </template>

                  <!-- the modal content -->
                  <v-card>
                    <v-card-text class="pt-4">
                      <v-row>
                        <v-col
                          cols="12"
                          md="6"
                        >
                          <v-text-field
                            v-model="downloadForm.name"
                            label="Report Name"
                            :hide-details="!$v.downloadForm.name.$anyError"
                            :error-messages="$v.downloadForm.name.$anyError ? ['Please enter the report name'] : null"
                            outlined
                            dense
                          ></v-text-field>
                        </v-col>

                        <v-col
                          cols="12"
                          md="6"
                        >
                          <v-file-input
                            v-model="downloadForm.image"
                            label="Upload Logo"
                            accept="image/png, image/jpeg"
                            :truncate-length="computedComponentName === 'v-menu' ? 14 : null"
                            :hide-details="!$v.downloadForm.image.$anyError"
                            :error-messages="$v.downloadForm.image.$anyError ? ['Image must be under 100Kb'] : null"
                            outlined
                            dense
                          ></v-file-input>
                        </v-col>
                      </v-row>
                    </v-card-text>

                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        @click="initiateReportDownload(item)"
                        :disabled="isLoading || pendingRequests.includes(item.id)"
                      >
                        Download
                        <v-icon right>navigate_next</v-icon>
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </component>
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </template>

    <!-- Fallback -->
    <template v-else-if="!isLoading">
      <div class="text-center">
        No campaigns found.
      </div>
    </template>

    <!-- Create Campaign Form -->
    <create-campaign-form
      v-if="shouldShowFormDialog"
      @close="shouldShowFormDialog = false"
      @refresh="fetchItems"
    />

    <!-- Report PDF View -->
    <div
      v-if="pdfData"
      id="campaignReportPdfView"
      class="campaign-report-pdf-view white"
    >
      <!-- Page 1: Show campaign's information -->
      <div class="box d-flex flex-column justify-center align-center">
        <img
          v-if="pdfData.logo"
          class="campaign-logo mb-4"
          :src="storageUrl(pdfData.logo)"
        />

        <div class="text-h3">
          {{ pdfData.name }}
        </div>
      </div>

      <!-- Page 2+: Show individual post's information -->
      <div
        v-for="(item, index)  in pdfData.posts"
        :key="'campaign-post-' + index"
        class="box pa-3"
      >
        <v-row class="height-100 pt-6">
          <!-- show the post stats -->
          <v-col
            cols="4"
            class="pt-16 pl-12"
          >
            <!-- about the post's user -->
            <div class="d-flex align-center">
              <v-avatar
                v-if="item.user_avatar"
                size="50"
              >
                <img :src="proxyUrl(item.user_avatar, true)" />
              </v-avatar>

              <div class="d-flex flex-column ml-3">
                <div class="text-subtitle-2 line-height-1 mb-1 font-weight-bold">
                  @{{ item.user_id }}
                </div>
                <div class="text-subtitle-1 line-height-1">
                  {{ item.user_name }}
                </div>
                <div
                  v-if="item.user_followers"
                  class="text-caption"
                >
                  {{ nFormatter(item.user_followers) }} Followers
                </div>
              </div>
            </div>

            <v-divider class="my-6" />

            <!-- post stats -->
            <div class="pr-16">
              <div class="d-flex justify-space-between pr-12">
                <span>
                  Post KPI
                </span>

                <b>
                  {{ nFormatter(item.post_kpi) }}
                </b>
              </div>

              <div class="d-flex justify-space-between pr-12 mt-3">
                <span>
                  Post Likes
                </span>

                <b>
                  {{ nFormatter(item.post_likes) }}
                </b>
              </div>

              <!-- Hide this if the value is empty/null/zero -->
              <div
                v-if="item.post_views && item.post_views !== '0'"
                class="d-flex justify-space-between pr-12 mt-3"
              >
                <span>
                  Post Views
                </span>

                <b>
                  {{ nFormatter(item.post_views) }}
                </b>
              </div>

              <div class="d-flex justify-space-between pr-12 mt-3">
                <span>
                  Post Comments
                </span>

                <b>
                  {{ nFormatter(item.post_comments) }}
                </b>
              </div>
            </div>
          </v-col>
          <!-- show the post screenshot -->
          <v-col
            cols="8"
            class="d-flex height-100 align-center"
          >
            <img
              v-if="item.screenshot_path"
              class="campaign-screenshot box-shadow-soft"
              :src="storageUrl(item.screenshot_path)"
            />
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators"

// packages to generate and download the PDF
import download from 'downloadjs'

const CreateCampaignForm = () => import("@/components/campaigns/CreateForm.vue")

const defaultDownloadForm =  () => ({
  name: "",
  image: null
})

export default {
  name: "CampaignIndex",

  components: {
    CreateCampaignForm
  },

  data: () => ({
    isLoading: false,

    // whether to show the create campaign form or not
    pdfData: null,
    shouldShowFormDialog: false,
    shouldShowDownloadModal: {},

    // to filter data in table
    selectedFilter: "All",
    filterOptions: [
      "All",
      "Active",
      "Completed"
    ],

    pendingRequests: [],
    downloadForm: defaultDownloadForm(),

    // related to table view
    tableHeaders: [
      {
        text: "Sr. No.",
        value: "index",
        sortable: false,
      },
      {
        text: "Campaign Name",
        value: "name",
        sortable: false,
      },
      {
        text: "Status",
        value: "is_complete",
        sortable: false,
      },
      {
        text: "Details",
        value: "action_details",
        sortable: false,
      },
      {
        text: "Download",
        value: "action_download",
        sortable: false,
      }
    ],
  }),

  computed: {
    /**
     * Get the data from vuex state
     */
    tableItems() {
      return this.$store.getters["campaigns/items"]
        .filter((item) => {
          if (this.selectedFilter === "All") {
            return true
          }
          else if (this.selectedFilter === "Active" && item.is_complete === false) {
            return true
          }
          else if (this.selectedFilter === "Completed" && item.is_complete === true) {
            return true
          } else {
            return false
          }
        })
        .map((item, index) => ({
          ...item,
          index: index + 1
        }))
    },

    /**
     * Compute whether to show a dialog or menu based on the device in use
     */
    computedComponentName() {
      return this.isDesktopDevice ? "v-menu" : "v-dialog"
    }
  },

  validations: {
    downloadForm: {
      name: {
        required,
      },

      image: {
        maxSize: (value) => {
          return value ? value.size <= 100_000 : true
        },
        mimeType: (value) => {
          return value ? ["image/png", "image/jpeg"].includes(value.type) : true
        }
      }
    }
  },

  methods: {
    /**
     * Whenever the select value for status is changed
     */
    async fetchItems() {
      // hide the form
      this.shouldShowFormDialog = false

      // show a loader
      this.isLoading = true

      await this.$store.dispatch("campaigns/fetchItems")

      this.isLoading = false
    },

    /**
     * Handle "View Details" click
     */
    openDescription(item) {
      this.$router.push({
        name: "CampaignDetails",
        params: {
          campaignId: item.id
        }
      })
    },

    /**
     * Methods related to download form
     */
    resetDownloadForm() {
      this.downloadForm = defaultDownloadForm()
      this.$v.$reset()
    },

    /**
     * When the modal/dialog is closed, reset the dialog form values
     *
     * @param {Boolean} value
     */
    handleModalStateChange(value) {
      if (value === false) {
        this.resetDownloadForm()
      }
    },

    /**
     * Remove an item from pending requests array
     *
     * @param {Number} id
     */
    removePendingRequestsItem(id) {
      const index = this.pendingRequests.findIndex((search) => search === id)

      if (index !== -1) {
        this.pendingRequests.splice(index, 1)
      }
    },

    /**
     * Make a download request using the form for the selected campaign Item
     *
     * @param {Object} item
     */
    async initiateReportDownload(item) {
      // check if a request is pending for this item
      if (this.pendingRequests.includes(item.id)) {
        return false
      }

      // push the item to pending requests
      this.pendingRequests.push(item.id)

      await this.$v.$touch()

      // if the validation fails anywhere, don't continue
      if (this.$v.$anyError) return

      // otherwise make the request
      this.isLoading = true

      const loaderId = Symbol()
      await this.$store.dispatch("loaders/add", loaderId, { root: true })

      // check if we need to upload any logo
      if (this.downloadForm.image) {
        try {
          const formData = new FormData()

          // append multipart data to formData
          formData.append("file", this.downloadForm.image)

          await axios({
            url: `/api/campaigns/${item.id}/logo`,
            method: "POST",
            data: formData
          })
        } catch (error) {
          logger({ type: "Error while uploading Logo", error })

          this.$store.dispatch("toasts/add", { text: "Logo could not be uploaded." })
        }
      }

      // now make request to capture all the screenshots
      try {
        await axios({
          url: `/api/campaigns/${item.id}/report`,
          method: "POST"
        })

        // alert the user
        this.$store.dispatch("toasts/add", { text: "Generating your report, please wait..." })

        // if the request was successful, start listening to the laravel echo connection
        window.echo
          .private(`campaigns.${item.id}`)
          .listen('CampaignReadyToBeDownloaded', (e) => {
            // once the data is available, try generating the final report data
            this.generateReport(e.campaign, this.downloadForm.name)

            // also stop listening to this event
            window.echo.leave(`campaigns.${item.id}`)

            // reset the download form values
            this.resetDownloadForm()
          })
      } catch (error) {
        logger({ type: "Error while generating report", error })

        this.$store.dispatch("toasts/add", { text: "Report could not be generated." })

        // remove the pending request item
        this.removePendingRequestsItem(item.id)
      } finally {
        // hide the progress loader
        await this.$store.dispatch("loaders/remove", loaderId)
        // hide the download popup
        this.shouldShowDownloadModal[item.id] = false
        // hide local loader
        this.isLoading = false
      }
    },

    /**
     * Generate the PDF for this report
     *
     * @param {Object} campaignData
     * @param {String} reportName
     */
    generateReport(campaignData, reportName) {
      // Alert the user
      this.$store.dispatch("toasts/add", { text: "This service is down for maintenance, please check back later." })
    }
  },

  mounted() {
    // Dispatch fetch method for campaigns from API
    this.fetchItems()
  }
}
</script>

<style lang="stylus" scoped>
.contain-select-width
  width 10em
</style>

<style lang="stylus">
.custom-table--campaigns-list
  colgroup
    col
      &:first-child
        width 10%
      &:nth-child(2)
        width 50%
      &:nth-child(3)
        width 20%
      &:last-child
        width 20%

  thead
    th
      &:nth-child(3), &:nth-child(4), &:last-child
        text-align center !important

.campaign-report-pdf-view
  position fixed
  z-index -10000
  left 0
  top 0
  height 100%
  width 1400px
  overflow-y auto

  .box
    width 100%
    height 700px
    // border 2px solid red

    .campaign-logo
      display block
      height 120px
      object-fit contain

    .campaign-screenshot
      display block
      width 100%
      max-height 600px
      object-fit contain
</style>
